import React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useTranslation } from 'src/i18n';
import { Collapsible, CollapsibleContent } from 'src/component/ds/ui/collapsible';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub
} from 'src/component/ds/ui/sidebar';
import type { DeviceNavNode as DeviceNavNodeData } from 'src/service/api/deviceNav';
import DeviceNavCount from './DeviceNavCount';
import DeviceNavLeaf from './DeviceNavLeaf';
import DeviceNavRowAction from './DeviceNavRowAction';
import { navRowClassName } from './navRow';

interface DeviceNavNodeProps {
  node: DeviceNavNodeData;
  label: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** True when the node itself is selected, not one of its rows. */
  active: boolean;
  activeChildId?: string;
  onSelect: () => void;
  onSelectChild: (childId: string) => void;
}

/* A panel, group or connection. Clicking selects and expands it; clicking it again while selected collapses it. */
export default function DeviceNavNode ({
  node,
  label,
  expanded,
  onExpandedChange,
  active,
  activeChildId,
  onSelect,
  onSelectChild
}: DeviceNavNodeProps) {
  const { t } = useTranslation();
  const hasChildren = node.children.length > 0;

  const handleClick = () => {
    if (active && expanded) {
      onExpandedChange(false);
      return;
    }
    onSelect();
    onExpandedChange(true);
  };

  return (
    <Collapsible asChild open={expanded && hasChildren} onOpenChange={onExpandedChange}>
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={active}
          aria-current={active ? 'true' : undefined}
          aria-expanded={hasChildren ? expanded : undefined}
          onClick={handleClick}
          className={navRowClassName}
          title={label}
        >
          <span>{label}</span>
        </SidebarMenuButton>
        <DeviceNavCount count={node.children.length} className='ds-right-9' />
        {/* Stub: the row's menu isn't built yet. */}
        <DeviceNavRowAction label={t('deviceNav.more', { name: label })} icon={<MoreVertIcon aria-hidden='true' />} />
        <CollapsibleContent>
          <SidebarMenuSub className='ds-mx-0 ds-ml-[18px] ds-gap-0 ds-py-0 ds-pl-[9px] ds-pr-0'>
            {node.children.map((child) => (
              <DeviceNavLeaf
                key={child.id}
                tag={child.tag}
                childCount={child.kind === 'controller' ? child.childCount : undefined}
                active={child.id === activeChildId}
                onSelect={() => onSelectChild(child.id)}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
